import React, { useState } from 'react';
import axios from 'axios';
import _ from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Switch from '~/components/ui/atoms/Switch';

import { reportAxiosError } from '../../../../Utils';
import CardDialog from '../../../CardDialog';
import { useCms } from '../../../hooks/useCms';
import LoadingIndicator from '../../../LoadingIndicator';
import ApprovalFinances from '../ApprovalsFinances';

import CurrentBalances from './CurrentBalances';
import Filters, { FILTER_KEYS } from './Filters';

const PaymentSubmissions = () => {
  const { userOrganization } = useCms();
  const [filters, setFilters] = useState({
    [FILTER_KEYS.payment_method]: 'commerce_bank',
    [FILTER_KEYS.payable_types_filter]: [],
    [FILTER_KEYS.sub_organization_id]: '',
    [FILTER_KEYS.balance_key]: '',
    [FILTER_KEYS.sub_organization_ids]: [],
    [FILTER_KEYS.coverage_config_ids]: [],
    [FILTER_KEYS.excluded_coverage_config_ids]: [],
  });
  const [page, setPage] = useState(0);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [isErrorBalances, setIsErrorBalances] = useState(null);
  const [isLoadingFinances, setIsLoadingFinances] = useState(false);
  const [isErrorFinances, setIsErrorFinances] = useState(null);
  const [statuses] = useState(['in_progress']);
  const [selectPaymentsEnabled, setSelectPaymentsEnabled] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBalancesReloadNeeded, setIsBalancesReloadNeeded] = useState(false);
  const [isFinancesReloadNeeded, setIsFinancesReloadNeeded] = useState(false);
  const [accountKeysAndDisplayNames, setAccountKeysAndDisplayNames] = useState({});

  const isLoading = isLoadingBalances || isLoadingFinances;
  const isError = isErrorBalances || isErrorFinances;

  const handleUpdateFilters = (newFilters) => {
    setFilters((prevState) => {
      const updatedFilters = { ...prevState };
      Object.keys(newFilters).forEach((key) => {
        updatedFilters[key] = newFilters[key];
      });

      return updatedFilters;
    });
    setPage(0);
  };

  const handleUpdatedSelectedPayments = (payments) => {
    setSelectedPayments(payments);
  };

  const handleSelectPaymentsEnabledChanged = (newValue) => {
    setSelectPaymentsEnabled(newValue);
    setSelectedPayments([]);
  };

  const refreshPage = () => {
    setPage(0);
    setIsFinancesReloadNeeded(true);
    setIsBalancesReloadNeeded(true);
  };

  const handleSubmitPayments = async () => {
    setIsSubmitting(true);
    try {
      const paymentIds = selectedPayments.map((p) => p.id);
      await axios.post(`/api/v1/organizations/${userOrganization.id}/payment_requests/submit`, paymentIds);
      refreshPage();
    } catch (error) {
      await reportAxiosError(error);
    }
    setIsSubmitting(false);
    handleSelectPaymentsEnabledChanged(false);
  };

  return (
    <CardDialog noCardTitle>
      <div className="p-20">
        <div className="pb-20">
          <Switch
            disabled={isLoading || isError || isSubmitting}
            checked={selectPaymentsEnabled}
            id="select_payments_enabled"
            onChange={() => handleSelectPaymentsEnabledChanged(!selectPaymentsEnabled)}
          />
          Enabled select payments
        </div>
        <div>
          <Filters
            filters={filters}
            onUpdateFilters={handleUpdateFilters}
            disabled={isLoading || isError}
            accountKeysAndDisplayNames={accountKeysAndDisplayNames}
          />
        </div>
        <div>
          <CurrentBalances
            setIsLoading={setIsLoadingBalances}
            setIsError={setIsErrorBalances}
            selectedPayments={selectedPayments}
            shouldReload={isBalancesReloadNeeded}
            onReload={() => setIsBalancesReloadNeeded(false)}
            setAccountKeysAndDisplayNames={setAccountKeysAndDisplayNames}
            filters={filters}
          />
        </div>
        <div>
          <ApprovalFinances
            filters={filters}
            statuses={statuses}
            page={page}
            setPage={setPage}
            onIsLoadingChanged={setIsLoadingFinances}
            onIsErrorChanged={setIsErrorFinances}
            enableSelection={selectPaymentsEnabled}
            selectedFinances={selectedPayments}
            onUpdatedSelectedFinances={handleUpdatedSelectedPayments}
            shouldReload={isFinancesReloadNeeded}
            onReload={() => setIsFinancesReloadNeeded(false)}
          />
        </div>
        <div>{(isLoading || isError) && <LoadingIndicator isError={isError} />}</div>
      </div>
      {selectPaymentsEnabled && (
        <ButtonsContainer>
          <CancelButton
            disabled={isSubmitting}
            onClick={() => {
              handleSelectPaymentsEnabledChanged(false);
            }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting || _.isEmpty(selectedPayments)}
            onClick={handleSubmitPayments}
          >
            SEND PAYMENTS
          </Button>
        </ButtonsContainer>
      )}
    </CardDialog>
  );
};

export default PaymentSubmissions;
